import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Stack,
  TextField,
  InputAdornment,
  Badge,
  Pagination,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Search as SearchIcon,
  Message as MessageIcon,
  ChatBubbleOutline as ChatIcon,
  ReplyAll as ReplyIcon,
  Block as BlockIcon,
  Flag as FlagIcon,
  Star as StarIcon,
  FileDownload as FileDownloadIcon,
  Info as InfoIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import { getCookie } from "../../../utils/storageUtils";
import axios from "axios";
import { toast } from "react-toastify";
import { formatDistanceToNow, format, parseISO } from "date-fns";
import { useGetUserDetailsMutation } from "redux/api/authApi";

// ── Theme constants ── (dark theme matching billing page)
const cardBg = "#161b22";
const border = "#21262d";
const accent = "#3b82f6";
const accentLight = "#60a5fa";
const mutedText = "#9ca3af";
const bgColor = "transparent";
const textPrimary = "#ffffff";
const textSecondary = "#9ca3af";

// ── Types matching backend responses ──
interface Enquiry {
  id: string;
  message: string;
  subject?: string;
  phone?: string;
  sender_id: string;
  sender_name: string;
  sender_email: string;
  sender_avatar?: string;
  created_at: string;
  is_read: boolean;
  is_important?: boolean;
}

/**
 * Backend /followers returns:
 * { status, data: [{ id, follower_id, name, email }] }
 * NOTE: is_following_back is computed client-side by checking the following list.
 */
interface Follower {
  id: string;             // user._id
  follower_id: string;   // same as id (user who follows us)
  follower_name: string;
  follower_email: string;
  user_image?: string;
  followed_at: string;
  is_following_back: boolean; // derived client-side
  mutual_follow?: boolean;
}

/**
 * Backend /following returns:
 * { status, data: [{ id, name, email }] }
 */
interface Following {
  id: string;            // user._id — used as following_id for unfollow action
  following_id: string;
  following_name: string;
  following_email: string;
  user_image?: string;
  followed_at: string;
}

interface FollowCounts {
  followers_count: number;
  following_count: number;
}

// ─── Shared UI helpers ────────────────────────────────────────────────────────
const LightCard = ({
  children, noPad, sx,
}: {
  children: React.ReactNode; noPad?: boolean; sx?: object;
}) => (
  <Box sx={{
    background: cardBg,
    borderRadius: "14px",
    border: `1px solid ${border}`,
    overflow: "hidden",
    ...(noPad ? {} : { p: 2.5 }),
    ...sx,
  }}>
    {children}
  </Box>
);

const MessagesAndFollowers: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [followers, setFollowers] = useState<Follower[]>([]);
  const [following, setFollowing] = useState<Following[]>([]);
  const [followCounts, setFollowCounts] = useState<FollowCounts>({
    followers_count: 0,
    following_count: 0,
  });
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);

  const [loading, setLoading] = useState({
    enquiries: false,
    followers: false,
    following: false,
    followAction: false,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const instructor_id = localStorage.getItem("userid");
  const user_id = localStorage.getItem("userid");
  const user_email = localStorage.getItem('user_email');
  const API_BASE_URL = process.env.REACT_APP_SERVER_ENDPOINT || "";
  const [getUserDetailsMutation] = useGetUserDetailsMutation();
  const [userDets, setUserDets] = useState<any>();


  const fetchData = async () => {
    try {
      const response = await getUserDetailsMutation({
        email: user_email || getCookie("user_email") || "",
        filter: "",
        start_date: "",
        end_date: "",
      });
      if ("data" in response) {
        setUserDets(response.data.data);
        console.log('user details response from message and leads res =>', response.data.data);
        localStorage.setItem('userid', response.data.data._id);
      }
    } catch (error) { }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ── Fetch Enquiries ──────────────────────────────────────────────────────
  const fetchEnquiries = useCallback(async () => {
    const token = getCookie("token");
    if (!instructor_id) return;
    setLoading(prev => ({ ...prev, enquiries: true }));
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/auth/enquiries/${instructor_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.data) {
        const mappedData = response.data.data.map((item: any) => ({
          id: item._id,
          message: item.message,
          subject: item.subject,
          phone: item.phone,
          sender_id: item.instructorid,
          sender_name: item.name,
          sender_email: item.email,
          sender_avatar: "",
          created_at: item.date,
          is_read: false,
        }));
        setEnquiries(mappedData);
      }
    } catch (error: any) {
      toast.error("Failed to load messages");
    } finally {
      setLoading(prev => ({ ...prev, enquiries: false }));
    }
  }, [instructor_id, API_BASE_URL]);

  // ── Fetch Following (needed before followers to compute is_following_back) ──
  const fetchFollowing = useCallback(async () => {
    if (!user_id) return;
    setLoading(prev => ({ ...prev, following: true }));
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/following`,
        { user_id },
        { headers: { "Content-Type": "application/json" } }
      );

      const raw: any[] = response.data?.data ?? response.data?.following ?? [];
      const mapped: Following[] = raw.map((item: any) => ({
        id: item.id ?? item._id ?? "",
        following_id: item.id ?? item._id ?? "",
        following_name: item.name ?? item.following_name ?? "",
        following_email: item.email ?? item.following_email ?? "",
        user_image: item.user_image ?? item.user_image ?? "",
        followed_at: item.followed_at ?? item.created_at ?? new Date().toISOString(),
      }));
      setFollowing(mapped);
      return mapped; // return so fetchFollowers can use it
    } catch (error: any) {
      console.error("Following ERROR =>", error.response);
      return [];
    } finally {
      setLoading(prev => ({ ...prev, following: false }));
    }
  }, [user_id, API_BASE_URL]);

  // ── Fetch Followers ──────────────────────────────────────────────────────
  const fetchFollowers = useCallback(async (currentFollowing?: Following[]) => {
    if (!user_id) return;
    setLoading(prev => ({ ...prev, followers: true }));
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/followers`,
        { user_id },
        { headers: { "Content-Type": "application/json" } }
      );


      const raw: any[] = response.data?.data ?? response.data?.followers ?? [];
      const followingIds = new Set(
        (currentFollowing ?? following).map(f => f.following_id)
      );

      const mapped: Follower[] = raw.map((item: any) => {
        const followerId = item.id ?? item._id ?? item.follower_id ?? "";
        const isFollowingBack = followingIds.has(followerId);
        return {
          id: followerId,
          follower_id: followerId,
          follower_name: item.name ?? item.follower_name ?? "",
          follower_email: item.email ?? item.follower_email ?? "",
          user_image: item.user_image ?? item.user_image ?? "",
          followed_at: item.followed_at ?? item.created_at ?? new Date().toISOString(),
          is_following_back: isFollowingBack,
          mutual_follow: isFollowingBack,
        };
      });
      setFollowers(mapped);
    } catch (error: any) {
      console.error("Followers ERROR =>", error.response);
    } finally {
      setLoading(prev => ({ ...prev, followers: false }));
    }
  }, [user_id, API_BASE_URL, following]);

  // ── Fetch Follow Counts ──────────────────────────────────────────────────
  const fetchFollowCounts = useCallback(async () => {
    if (!user_id) return;
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/follow-counts`,
        { user_id },
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.data) {
        setFollowCounts({
          followers_count: response.data.followers_count ?? 0,
          following_count: response.data.following_count ?? 0,
        });
      }
    } catch (error: any) {
      console.error("FollowCounts ERROR =>", error.response);
    }
  }, [user_id, API_BASE_URL]);

  // ── Follow ───────────────────────────────────────────────────────────────
  const handleFollow = async (targetUserId: string, targetUserName: string) => {
    setLoading(prev => ({ ...prev, followAction: true }));
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/follow`,
        { follower_id: instructor_id, following_id: targetUserId },
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.status === 200 || response.status === 201) {
        toast.success(`You are now following ${targetUserName}`);
        const updatedFollowing = await fetchFollowing();
        fetchFollowers(updatedFollowing);
        fetchFollowCounts();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to follow user");
    } finally {
      setLoading(prev => ({ ...prev, followAction: false }));
    }
  };

  // ── Unfollow ─────────────────────────────────────────────────────────────
  const handleUnfollow = async (targetUserId: string, targetUserName: string) => {
    setLoading(prev => ({ ...prev, followAction: true }));
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/unfollow`,
        { follower_id: instructor_id, following_id: targetUserId },
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.status === 200) {
        toast.success(`You unfollowed ${targetUserName}`);
        const updatedFollowing = await fetchFollowing();
        fetchFollowers(updatedFollowing);
        fetchFollowCounts();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to unfollow user");
    } finally {
      setLoading(prev => ({ ...prev, followAction: false }));
    }
  };

  // ── Initial load: fetch following first so followers can derive is_following_back ──
  useEffect(() => {
    if (user_id) {
      fetchEnquiries();
      fetchFollowCounts();
      fetchFollowing().then((followingData) => {
        fetchFollowers(followingData ?? []);
      });
    }
  }, [user_id]);

  // ── Reset page on tab/search change ─────────────────────────────────────
  useEffect(() => { setPage(1); }, [activeTab, searchTerm]);

  // ── Filtered data ────────────────────────────────────────────────────────
  const filteredEnquiries = enquiries.filter(e =>
    e.sender_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.message?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredFollowers = followers.filter(f =>
    f.follower_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredFollowing = following.filter(f =>
    f.following_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPaginatedData = (data: any[]) =>
    data.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handleMenuClose = () => setAnchorEl(null);

  // ── Export Handlers ───────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const headers = ["Name", "Email", "Phone", "Subject", "Message", "Date"];
    const rows = filteredEnquiries.map(e => [
      e.sender_name,
      e.sender_email,
      e.phone || "",
      e.subject || "",
      `"${(e.message || "").replace(/"/g, '""')}"`,
      e.created_at ? format(parseISO(e.created_at), "PPPpp") : "",
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "messages.csv";
    a.click();
    URL.revokeObjectURL(url);
    setExportAnchorEl(null);
    toast.success("Exported as CSV");
  };

  const handleExportJSON = () => {
    const data = filteredEnquiries.map(e => ({
      name: e.sender_name,
      email: e.sender_email,
      phone: e.phone || "",
      subject: e.subject || "",
      message: e.message,
      date: e.created_at,
    }));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "messages.json";
    a.click();
    URL.revokeObjectURL(url);
    setExportAnchorEl(null);
    toast.success("Exported as JSON");
  };

  // ─── Stats Cards ──────────────────────────────────────────────────────────
  const StatsCards = () => (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3.5}>
      {[
        { icon: <ChatIcon sx={{ fontSize: 20, color: accent }} />, value: enquiries.length, label: "Total Messages", subtitle: "People who sent you messages" },
        { icon: <PersonAddIcon sx={{ fontSize: 20, color: accent }} />, value: followCounts.followers_count, label: "Leads", subtitle: "People who followed you or subscribed" },
      ].map((stat, i) => (
        <LightCard key={i} sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "10px",
              background: "rgba(59,130,246,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              {stat.icon}
            </Box>
            <Box>
              <Typography fontSize="22px" fontWeight={700} color={textPrimary} lineHeight={1.1}>
                {stat.value}
              </Typography>
              <Typography fontSize="13px" fontWeight={500} color={textPrimary} mt={0.2}>{stat.label}</Typography>
              <Typography fontSize="11.5px" color={mutedText} mt={0.2}>{stat.subtitle}</Typography>
            </Box>
          </Stack>
        </LightCard>
      ))}
    </Stack>
  );

  // ─── Messages List ────────────────────────────────────────────────────────
  const MessagesList = () => {
    const unreadCount = enquiries.filter(e => !e.is_read).length;
    return (
      <>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2.5}>
          {/* LEFT: list */}
          <LightCard noPad sx={{ flex: 1.2 }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${border}` }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography fontWeight={600} fontSize="14px" color={textPrimary}>Messages</Typography>
                {unreadCount > 0 && (
                  <Box sx={{
                    px: 1.5, py: 0.35,
                    background: "rgba(59,130,246,0.15)", border: `1px solid rgba(59,130,246,0.3)`,
                    borderRadius: "7px", color: accent, fontSize: "11px", fontWeight: 600,
                  }}>
                    {unreadCount} unread
                  </Box>
                )}
              </Stack>
            </Box>

            <Box sx={{
              maxHeight: "calc(100vh - 250px)", overflowY: "auto", overflowX: "hidden",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": { background: "#30363d", borderRadius: "10px" },
            }}>
              {loading.enquiries ? (
                <Box display="flex" justifyContent="center" py={5}>
                  <CircularProgress size={28} sx={{ color: accent }} />
                </Box>
              ) : getPaginatedData(filteredEnquiries).length === 0 ? (
                <Box py={6} textAlign="center">
                  <MessageIcon sx={{ fontSize: 40, color: "#4b5563", mb: 1 }} />
                  <Typography sx={{ color: mutedText, fontSize: "13px", textAlign: "center" }}>No messages found</Typography>
                </Box>
              ) : (
                getPaginatedData(filteredEnquiries).map((enquiry) => (
                  <Box
                    key={enquiry.id}
                    onClick={() => setSelectedEnquiry(enquiry)}
                    sx={{
                      px: 2.5, py: 2, borderBottom: `1px solid ${border}`,
                      cursor: "pointer",
                      background: selectedEnquiry?.id === enquiry.id ? "rgba(59,130,246,0.08)" : "transparent",
                      "&:hover": { background: "rgba(59,130,246,0.05)" },
                      transition: "background 0.15s",
                    }}
                  >
                    <Stack direction="row" spacing={1.5}>
                      <Badge
                        overlap="circular" variant="dot" invisible={enquiry.is_read}
                        sx={{ "& .MuiBadge-badge": { background: "#3b82f6", width: 8, height: 8 } }}
                      >
                        <Avatar src={enquiry.sender_avatar} sx={{
                          width: 44, height: 44,
                          background: "linear-gradient(135deg, #1d4ed8, #3b82f6)",
                          fontSize: "15px", fontWeight: 600,
                        }}>
                          {enquiry.sender_name?.[0] || "U"}
                        </Avatar>
                      </Badge>
                      <Box flex={1} minWidth={0}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography fontWeight={600} color={textPrimary} fontSize="13.5px">
                            {enquiry.sender_name}
                          </Typography>
                          <Typography fontSize="11px" color={mutedText}>
                            {enquiry.created_at
                              ? formatDistanceToNow(parseISO(enquiry.created_at), { addSuffix: true })
                              : ""}
                          </Typography>
                        </Stack>
                        <Typography fontSize="12.5px" color={textSecondary} noWrap>
                          {enquiry.subject}
                        </Typography>
                        {enquiry.is_important && (
                          <StarIcon sx={{ fontSize: 12, color: "#f59e0b", mt: 0.5 }} />
                        )}
                      </Box>
                    </Stack>
                  </Box>
                ))
              )}
            </Box>

            {filteredEnquiries.length > 0 && (
              <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${border}` }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography fontSize="12px" color={mutedText}>
                    Showing {Math.min((page - 1) * itemsPerPage + 1, filteredEnquiries.length)}–{Math.min(page * itemsPerPage, filteredEnquiries.length)} of {filteredEnquiries.length} messages
                  </Typography>
                  {filteredEnquiries.length > itemsPerPage && (
                    <Pagination
                      count={Math.ceil(filteredEnquiries.length / itemsPerPage)}
                      page={page}
                      onChange={(_, value) => setPage(value)}
                      size="small"
                      sx={{
                        "& .MuiPaginationItem-root": { color: "#666666", fontSize: "12px" },
                        "& .Mui-selected": { background: "#3b82f6 !important", color: "#fff" },
                      }}
                    />
                  )}
                </Stack>
              </Box>
            )}
          </LightCard>

          {/* RIGHT: detail */}
          <LightCard sx={{ flex: 1.8, minHeight: "500px", display: "flex", flexDirection: "column" }}>
            {selectedEnquiry ? (
              <>
                <Box sx={{ p: 2.5, borderBottom: `1px solid ${border}` }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ width: 48, height: 48, background: "linear-gradient(135deg, #1d4ed8, #3b82f6)" }}>
                      {selectedEnquiry.sender_name?.[0]}
                    </Avatar>
                    <Box>
                      <Typography fontWeight={600}>{selectedEnquiry.sender_name}</Typography>
                      <Typography fontSize="12px" color={mutedText}>{selectedEnquiry.sender_email}</Typography>
                    </Box>
                  </Stack>
                </Box>
                <Box sx={{ p: 3, flex: 1 }}>
                  <Typography fontSize="16px" fontWeight={600} mb={2}>
                    {selectedEnquiry.subject || "No Subject"}
                  </Typography>
                  <Stack spacing={1} mb={2}>
                    <Typography fontSize="13px"><b>Name:</b> {selectedEnquiry.sender_name}</Typography>
                    <Typography fontSize="13px"><b>Email:</b> {selectedEnquiry.sender_email}</Typography>
                    <Typography fontSize="13px"><b>Phone:</b> {selectedEnquiry.phone || "-"}</Typography>
                  </Stack>
                  <Box sx={{ background: "#0d1117", borderRadius: "10px", p: 2, mb: 2 }}>
                    <Typography fontSize="14px">{selectedEnquiry.message}</Typography>
                  </Box>
                  <Typography fontSize="12px" color={mutedText}>
                    Received {selectedEnquiry.created_at
                      ? format(parseISO(selectedEnquiry.created_at), "PPPpp")
                      : ""}
                  </Typography>
                </Box>
              </>
            ) : (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flex={1} gap={2}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="110" height="100" viewBox="0 0 110 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M55 8C29.6 8 9 25.9 9 48C9 58.4 13.6 67.9 21.3 74.9L16 92L34.5 82.8C40.8 85 47.7 86.2 55 86.2C80.4 86.2 101 68.3 101 46.1C101 23.9 80.4 8 55 8Z"
                      stroke="#6b7280" strokeWidth="4" strokeLinejoin="round" fill="none"
                    />
                    <circle cx="38" cy="48" r="4.5" fill="#6b7280" />
                    <circle cx="55" cy="48" r="4.5" fill="#6b7280" />
                    <circle cx="72" cy="48" r="4.5" fill="#6b7280" />
                  </svg>
                </Box>
                <Box textAlign="center">
                  <Typography fontSize="16px" fontWeight={600} color={textPrimary} mb={0.75}>
                    Select a message to view
                  </Typography>
                  <Typography fontSize="13px" color={mutedText} lineHeight={1.7}>
                    Choose a message from the list to see the details.<br />
                    This is where you'll find messages from people<br />
                    who contacted you through your Profile Page or Blog.
                  </Typography>
                </Box>
              </Box>
            )}
          </LightCard>
        </Stack>

        {/* Important Note */}
        <Box sx={{
          mt: 2.5,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          background: "rgba(59,130,246,0.06)",
          border: "1px solid rgba(59,130,246,0.18)",
          borderRadius: "12px",
          px: 2.5,
          py: 1.75,
        }}>
          <InfoIcon sx={{ fontSize: 17, color: accent, mt: 0.15, flexShrink: 0 }} />
          <Typography fontSize="13px" color={mutedText} lineHeight={1.6}>
            <Box component="span" sx={{ color: accent, fontWeight: 600 }}>Important: </Box>
            These messages and leads details are collected on your behalf. We do not share this information with anyone.
          </Typography>
        </Box>
      </>
    );
  };

  // ─── Followers List ───────────────────────────────────────────────────────
  const FollowersList = () => (
    <LightCard noPad>
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${border}` }}>
        <Typography fontWeight={600} fontSize="14px" color={textPrimary}>Your Leads</Typography>
        <Typography fontSize="12px" color={mutedText} mt={0.25}>People who leads you</Typography>
      </Box>

      {loading.followers ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} sx={{ color: accent }} />
        </Box>
      ) : getPaginatedData(filteredFollowers).length === 0 ? (
        <Box py={6} textAlign="center">
          <PersonAddIcon sx={{ fontSize: 40, color: "#4b5563", mb: 1 }} />
          <Typography sx={{ color: mutedText, fontSize: "13px", textAlign: "center" }}>No Leads yet</Typography>
        </Box>
      ) : (
        <Box>
          {getPaginatedData(filteredFollowers).map((follower, index) => (
            <React.Fragment key={follower.id}>
              <Box sx={{ px: 2.5, py: 2, "&:hover": { background: "rgba(59,130,246,0.05)" }, transition: "background 0.15s" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar src={follower.user_image} sx={{
                      width: 48, height: 48,
                      background: "linear-gradient(135deg, #1d4ed8, #3b82f6)",
                      fontSize: "16px", fontWeight: 600,
                    }}>
                      {follower.follower_name?.[0] || "U"}
                    </Avatar>
                    <Box>
                      <Typography fontWeight={600} color={textPrimary} fontSize="14px">
                        {follower.follower_name}
                      </Typography>
                      <Typography fontWeight={600} color={textSecondary} fontSize="10px">
                        {follower.follower_email}
                      </Typography>
                      <Typography fontSize="12px" color={mutedText}>
                        {follower.followed_at
                          ? `Followed ${formatDistanceToNow(new Date(follower.followed_at), { addSuffix: true })}`
                          : ""}
                      </Typography>
                      {follower.mutual_follow && (
                        <Box sx={{
                          display: "inline-flex", mt: 0.5, px: 1.25, py: 0.3,
                          background: "rgba(1,104,181,0.08)", border: "1px solid rgba(1,104,181,0.2)",
                          borderRadius: "6px", color: accent, fontSize: "11px", fontWeight: 500,
                        }}>
                          Mutual Follow
                        </Box>
                      )}
                    </Box>
                  </Stack>

                  {/* now this is not mandatory */}
                  {/* <Button
                    variant="outlined"
                    size="small"
                    startIcon={follower.is_following_back ? <PersonRemoveIcon /> : <PersonAddIcon />}
                    onClick={() =>
                      follower.is_following_back
                        ? handleUnfollow(follower.follower_id, follower.follower_name)
                        : handleFollow(follower.follower_id, follower.follower_name)
                    }
                    disabled={loading.followAction}
                    sx={{
                      borderRadius: "8px", textTransform: "none", fontSize: "12px", fontWeight: 500,
                      borderColor: follower.is_following_back ? "#e0e0e0" : accent,
                      color: follower.is_following_back ? "#666666" : accent,
                      "&:hover": {
                        borderColor: follower.is_following_back ? "#cccccc" : accentLight,
                        background: follower.is_following_back ? "rgba(0,0,0,0.03)" : "rgba(1,104,181,0.05)",
                      },
                    }}
                  >
                    {follower.is_following_back ? "Following" : "Follow Back"}
                  </Button> */}
                </Stack>
              </Box>
              {index < getPaginatedData(filteredFollowers).length - 1 && (
                <Divider sx={{ borderColor: border }} />
              )}
            </React.Fragment>
          ))}
        </Box>
      )}

      {filteredFollowers.length > itemsPerPage && (
        <Box display="flex" justifyContent="center" p={2.5}>
          <Pagination
            count={Math.ceil(filteredFollowers.length / itemsPerPage)}
            page={page}
            onChange={(_, value) => setPage(value)}
            size="small"
            sx={{
              "& .MuiPaginationItem-root": { color: "#9ca3af", fontSize: "12px" },
              "& .Mui-selected": { background: "#3b82f6 !important", color: "#fff" },
            }}
          />
        </Box>
      )}
    </LightCard>
  );

  // ─── Following List ───────────────────────────────────────────────────────
  const FollowingList = () => (
    <LightCard noPad>
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${border}` }}>
        <Typography fontWeight={600} fontSize="14px" color={textPrimary}>People You Follow</Typography>
        <Typography fontSize="12px" color={mutedText} mt={0.25}>Accounts you're following</Typography>
      </Box>

      {loading.following ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} sx={{ color: accent }} />
        </Box>
      ) : getPaginatedData(filteredFollowing).length === 0 ? (
        <Box py={6} textAlign="center">
          <PersonAddIcon sx={{ fontSize: 40, color: "#4b5563", mb: 1 }} />
          <Typography sx={{ color: mutedText, fontSize: "13px", textAlign: "center" }}>Not following anyone yet</Typography>
        </Box>
      ) : (
        <Box>
          {getPaginatedData(filteredFollowing).map((follow, index) => (
            <React.Fragment key={follow.id}>
              <Box sx={{ px: 2.5, py: 2, "&:hover": { background: "rgba(59,130,246,0.05)" }, transition: "background 0.15s" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar src={follow.user_image} sx={{
                      width: 48, height: 48,
                      background: "linear-gradient(135deg, #1d4ed8, #3b82f6)",
                      fontSize: "16px", fontWeight: 600,
                    }}>
                      {follow.following_name?.[0] || "U"}
                    </Avatar>
                    <Box>
                      <Typography fontWeight={600} color={textPrimary} fontSize="14px">
                        {follow.following_name}
                      </Typography>
                      <Typography fontWeight={600} color={textSecondary} fontSize="10px">
                        {follow.following_email}
                      </Typography>
                      <Typography fontSize="12px" color={mutedText}>
                        {follow.followed_at
                          ? `Followed ${formatDistanceToNow(new Date(follow.followed_at), { addSuffix: true })}`
                          : ""}
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PersonRemoveIcon />}
                    onClick={() => handleUnfollow(follow.following_id, follow.following_name)}
                    disabled={loading.followAction}
                    sx={{
                      borderRadius: "8px", textTransform: "none", fontSize: "12px", fontWeight: 500,
                      borderColor: "#30363d", color: "#9ca3af",
                      "&:hover": { borderColor: "#f87171", color: "#ef4444", background: "rgba(239,68,68,0.04)" },
                    }}
                  >
                    Unfollow
                  </Button>
                </Stack>
              </Box>
              {index < getPaginatedData(filteredFollowing).length - 1 && (
                <Divider sx={{ borderColor: border }} />
              )}
            </React.Fragment>
          ))}
        </Box>
      )}

      {filteredFollowing.length > itemsPerPage && (
        <Box display="flex" justifyContent="center" p={2.5}>
          <Pagination
            count={Math.ceil(filteredFollowing.length / itemsPerPage)}
            page={page}
            onChange={(_, value) => setPage(value)}
            size="small"
            sx={{
              "& .MuiPaginationItem-root": { color: "#9ca3af", fontSize: "12px" },
              "& .Mui-selected": { background: "#3b82f6 !important", color: "#fff" },
            }}
          />
        </Box>
      )}
    </LightCard>
  );

  // ─── Menu items ───────────────────────────────────────────────────────────
  const menuItems = [
    { icon: <ReplyIcon />, label: "Reply", action: () => { } },
    { icon: <BlockIcon />, label: "Block User", action: () => toast.info("User blocked") },
    { icon: <FlagIcon />, label: "Report", action: () => toast.info("Report submitted") },
  ];

  return (
    <Box sx={{
      minHeight: "100vh", background: "transparent", color: textPrimary,
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3.5}>
          <Box>
            <Typography fontSize="22px" fontWeight={600} mb={0.5} color={textPrimary}>
              Messages & Leads
            </Typography>
            <Typography color="#666666" fontSize="13px">
              View and manage the people who connect with you through your Profile Page or Blog.
            </Typography>
          </Box>
        </Stack>

        <StatsCards />

        {/* Tabs */}
        <Box sx={{ background: cardBg, borderRadius: "14px", border: `1px solid ${border}`, mb: 2.5, px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              minHeight: 48,
              "& .MuiTab-root": {
                color: mutedText, fontWeight: 500, fontSize: "13px",
                textTransform: "none", minHeight: 48,
                "&.Mui-selected": { color: "#3b82f6", fontWeight: 600 },
              },
              "& .MuiTabs-indicator": {
                background: "#3b82f6",
                borderRadius: "2px", height: "2.5px",
              },
            }}
          >
            <Tab label={`Messages (${enquiries.length})`} />
            <Tab label={`Leads (${followCounts.followers_count})`} />
            {/* <Tab label={`Following (${followCounts.following_count})`} /> */}
          </Tabs>
        </Box>

        {/* Search + Export row */}
        <Stack direction="row" spacing={1.5} mb={2.5} alignItems="center">
          <TextField
            fullWidth
            placeholder="Search by name or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                background: "#161b22", borderRadius: "10px", color: "#ffffff", fontSize: "13px",
                "& fieldset": { borderColor: "#21262d" },
                "&:hover fieldset": { borderColor: "#3b82f6" },
                "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
              },
              "& input::placeholder": { color: mutedText },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: mutedText, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
          {activeTab === 0 && (
            <>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                endIcon={<ArrowDownIcon />}
                onClick={(e) => setExportAnchorEl(e.currentTarget)}
                sx={{
                  whiteSpace: "nowrap",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 500,
                  borderColor: "#21262d",
                  color: textPrimary,
                  background: "#161b22",
                  px: 2,
                  height: 44,
                  "&:hover": { borderColor: accent, color: accent, background: "rgba(59,130,246,0.06)" },
                }}
              >
                Export
              </Button>
              <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={() => setExportAnchorEl(null)}
                PaperProps={{
                  sx: {
                    background: "#161b22", border: `1px solid #21262d`,
                    borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                    "& .MuiMenuItem-root": {
                      color: textPrimary, fontSize: "13px", gap: 1.5,
                      "&:hover": { background: "rgba(59,130,246,0.08)" },
                    },
                  },
                }}
              >
                <MenuItem onClick={handleExportCSV}>Export as CSV</MenuItem>
                <MenuItem onClick={handleExportJSON}>Export as JSON</MenuItem>
              </Menu>
            </>
          )}
        </Stack>

        {activeTab === 0 && <MessagesList />}
        {activeTab === 1 && <FollowersList />}
        {activeTab === 2 && <FollowingList />}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              background: "#161b22", border: `1px solid #21262d`,
              borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              "& .MuiMenuItem-root": {
                color: textPrimary, fontSize: "13px", gap: 1.5,
                "&:hover": { background: "rgba(59,130,246,0.08)" },
              },
            },
          }}
        >
          {menuItems.map((item, idx) => (
            <MenuItem key={idx} onClick={() => { item.action(); handleMenuClose(); }}>
              <Box sx={{ color: mutedText, display: "flex", alignItems: "center" }}>{item.icon}</Box>
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  );
};

export default MessagesAndFollowers;